import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Pill, Btn, Input, Textarea } from "../components/UI";
import { Building2, Trash2, Plus, Palette, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const listToText = (arr) => (arr || []).join("\n");
const textToList = (t) => String(t || "").split("\n").map((s) => s.trim()).filter(Boolean);

const BrandKitModal = ({ entity, onClose, onSaved }) => {
  const bk = entity.brand_kit || {};
  const [form, setForm] = useState({
    logo_url: bk.logo_url || "",
    slogans: listToText(bk.slogans),
    talking_points: listToText(bk.talking_points),
    key_issues: listToText(bk.key_issues),
    ctas: listToText(bk.ctas),
    tone: bk.tone || "",
    bio: bk.bio || "",
    fundraising_url: bk.fundraising_url || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/political-entities/${entity.id}/brand-kit`, {
        logo_url: form.logo_url || null,
        slogans: textToList(form.slogans),
        talking_points: textToList(form.talking_points),
        key_issues: textToList(form.key_issues),
        ctas: textToList(form.ctas),
        tone: form.tone || null,
        bio: form.bio || null,
        fundraising_url: form.fundraising_url || null,
      });
      toast.success("Brand kit saved — the AI will use it automatically");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save brand kit");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-app border border-white/10 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="brand-kit-modal">
        <h3 className="font-display text-2xl font-bold flex items-center gap-2">
          <Palette className="w-5 h-5 text-fuchsia-300" /> Brand Kit — {entity.public_campaign_name || entity.legal_org_name}
        </h3>
        <p className="text-xs text-slate-500 mt-1">The Campaign Agent, Ad Studio and landing-page AI use these automatically — set them once, never re-type them.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <Input label="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" data-testid="bk-logo-input" className="md:col-span-2" />
          <Textarea label="Slogans (one per line)" rows={3} value={form.slogans} onChange={(e) => setForm({ ...form, slogans: e.target.value })} placeholder={"Fighting for Texas families\nLeadership that listens"} data-testid="bk-slogans-input" />
          <Textarea label="Calls to action (one per line)" rows={3} value={form.ctas} onChange={(e) => setForm({ ...form, ctas: e.target.value })} placeholder={"Join us\nChip in $25\nGet involved"} data-testid="bk-ctas-input" />
          <Textarea label="Talking points (one per line)" rows={4} value={form.talking_points} onChange={(e) => setForm({ ...form, talking_points: e.target.value })} placeholder={"Cut property taxes for seniors\nFully fund public schools"} data-testid="bk-talking-points-input" />
          <Textarea label="Key issues (one per line)" rows={4} value={form.key_issues} onChange={(e) => setForm({ ...form, key_issues: e.target.value })} placeholder={"Property taxes\nEducation\nBorder security"} data-testid="bk-issues-input" />
          <Input label="Tone of voice" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} placeholder="Optimistic, plain-spoken, determined" data-testid="bk-tone-input" />
          <Input label="Fundraising URL" value={form.fundraising_url} onChange={(e) => setForm({ ...form, fundraising_url: e.target.value })} placeholder="https://…/donate" data-testid="bk-fundraising-input" />
          <div className="md:col-span-2">
            <Textarea label="Candidate / organization bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Short biography the AI can quote from…" data-testid="bk-bio-input" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="bk-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />} Save brand kit
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default function PoliticalOrganizations() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [editingBrand, setEditingBrand] = useState(null);
  const load = async () => { const { data } = await api.get("/political-entities"); setEntities(data); };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this political entity?")) return;
    await api.delete(`/political-entities/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Account · Entities"
        title="Political Organizations"
        subtitle="Every campaign and fundraiser inherits an entity's verification, disclaimer, authorization status and brand kit."
        action={<Btn onClick={() => navigate("/onboarding")} data-testid="new-org-btn"><Plus className="w-4 h-4" /> New entity</Btn>}
      />
      {entities.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <Building2 className="w-10 h-10 mx-auto text-slate-600" />
            <div className="mt-3 font-display text-lg">No entities yet</div>
            <Btn className="mt-3" onClick={() => navigate("/onboarding")}>Set up first entity</Btn>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {entities.map(e => (
            <Card key={e.id} data-testid={`org-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-xl font-bold">{e.public_campaign_name || e.legal_org_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 capitalize">{e.entity_type?.replace(/_/g, " ")} · {e.state || "—"} {e.district ? `· ${e.district}` : ""}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Pill status={e.verification_status === "verified" ? "Active" : "Pending"}>{e.verification_status}</Pill>
                  {e.brand_kit && Object.keys(e.brand_kit).some(k => e.brand_kit[k]?.length) && (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-200 border border-fuchsia-400/25" data-testid={`brand-kit-badge-${e.id}`}>Brand kit set</span>
                  )}
                </div>
              </div>
              {e.candidate_name && <div className="mt-2 text-sm text-slate-300">{e.candidate_name} · {e.office_sought}</div>}
              <div className="mt-3 text-xs text-slate-400"><span className="uppercase tracking-wider text-slate-500">Authorization:</span> {e.authorization_status?.replace(/_/g, " ")}</div>
              {e.approved_disclaimers?.[0] && <div className="mt-1 text-xs italic text-slate-400">"{e.approved_disclaimers[0]}"</div>}
              {e.brand_kit?.slogans?.[0] && <div className="mt-1 text-xs text-fuchsia-200/80 italic">"{e.brand_kit.slogans[0]}"</div>}
              <div className="mt-3 flex items-center justify-end gap-2">
                <Btn variant="secondary" onClick={() => setEditingBrand(e)} data-testid={`edit-brand-kit-${e.id}`}>
                  <Palette className="w-4 h-4" /> Brand Kit
                </Btn>
                <button onClick={() => del(e.id)} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {editingBrand && (
        <BrandKitModal entity={editingBrand} onClose={() => setEditingBrand(null)} onSaved={() => { setEditingBrand(null); load(); }} />
      )}
    </div>
  );
}
